/*
 * penguin.js — the site's only JavaScript.
 * Owned by WP-04. No dependencies, no build step.
 *
 * 1. Mobile menu. The mockup styled .mobile-menu-toggle but shipped no JS at all,
 *    so on a phone the nav never opened.
 * 2. FAQ accordion. The mockup inlined the same handler into 13 files, bound at
 *    parse time. Delegated here so it survives theme-editor re-renders.
 * 3. Dealer nav dropdowns. Only present in the markup for a `customer.b2b?`
 *    session; on every other page these selectors match nothing and the code
 *    costs one querySelectorAll.
 */
(function () {
  'use strict';

  /* ---------- Mobile menu ---------- */
  function initMobileMenu(root) {
    var toggle = root.querySelector('.mobile-menu-toggle');
    var nav = root.querySelector('#SiteNav');
    if (!toggle || !nav || toggle.dataset.penguinBound === '1') return;
    toggle.dataset.penguinBound = '1';

    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('site-nav--open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('site-nav--open')) {
        nav.classList.remove('site-nav--open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
        toggle.focus();
      }
    });
  }

  /* ---------- Dealer nav dropdowns ----------
   *
   * Markup: snippets/dealer-nav.liquid. Styling: the DEALER NAV block in
   * penguin-theme.css. Present only when `customer.b2b?` is true.
   *
   * One piece of state per group — the .is-open class. The CSS has no :hover
   * rule that opens a panel, on purpose: two sources of truth would let the panel
   * be on screen while the button still reported aria-expanded="false".
   *
   * There are two widths and they behave differently, which is the thing to hold
   * on to when reading the handlers below:
   *
   *   >= 1280px  OVERLAY. The panel floats over the page and only one may be
   *              open. Hover opens, focus opens, and anything that takes the
   *              pointer or the focus away closes it — leaving a floating card
   *              on screen after you have moved on is the failure mode here.
   *
   *   <  1280px  ACCORDION, nested inside the hamburger menu. The panel is in the
   *              document flow, both groups may be open at once, and ONLY the
   *              chevron and Escape change the state. Every "close it because
   *              attention moved elsewhere" rule is switched off, because each
   *              one re-flows the menu: closing a group above your finger drags
   *              the row you were reaching for several hundred pixels up between
   *              touch-down and tap, and the tap lands on whatever slid into
   *              that spot. That is a real mis-tap on a real iPad, not a
   *              theoretical one — it is what the 390px checks in
   *              tools/dealer-nav-qa.mjs caught.
   *
   * Hover is additionally gated on `(hover: hover)`. A tablet reports
   * `hover: none`, so an iPad gets the tap path and never a sticky hover-open it
   * cannot dismiss by moving a finger away.
   */
  function initDealerNav(root) {
    var groups = root.querySelectorAll('[data-dealer-nav-group]');
    if (!groups.length) return;

    var desktopLayout = window.matchMedia('(min-width: 1280px)');
    var desktopHover = window.matchMedia('(min-width: 1280px) and (hover: hover)');

    function setOpen(group, open) {
      group.classList.toggle('is-open', open);
      var toggle = group.querySelector('.dealer-nav__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', String(open));
    }

    /* Only one panel at a time, and only in overlay mode — see the note above. */
    function closeOthers(except) {
      if (!desktopLayout.matches) return;
      groups.forEach(function (g) {
        if (g !== except && g.classList.contains('is-open')) setOpen(g, false);
      });
    }

    groups.forEach(function (group) {
      if (group.dataset.penguinBound === '1') return;
      group.dataset.penguinBound = '1';

      var trigger = group.querySelector('.dealer-nav__trigger');
      var toggle = group.querySelector('.dealer-nav__toggle');

      // Set when Escape returns focus to the trigger, so the focusin that
      // .focus() fires does not immediately reopen what Escape just closed.
      var suppressFocusOpen = false;

      // The state at pointer-down, captured before focusin has had a chance to
      // open the panel. Without it, the first tap on a closed menu opens it on
      // focus and then the click event toggles it straight back shut.
      var openAtPress = false;

      group.addEventListener('focusin', function () {
        if (suppressFocusOpen) { suppressFocusOpen = false; return; }
        closeOthers(group);
        setOpen(group, true);
      });

      group.addEventListener('focusout', function (e) {
        if (!desktopLayout.matches) return;   // accordion mode: focus does not close
        if (group.contains(e.relatedTarget)) return;
        // Tabbing away from a panel the mouse is still sitting on should not
        // yank it shut underneath the pointer.
        if (desktopHover.matches && group.matches(':hover')) return;
        setOpen(group, false);
      });

      if (toggle) {
        toggle.addEventListener('pointerdown', function () {
          openAtPress = group.classList.contains('is-open');
        });

        // Enter and Space never fire pointerdown, and by the time the button has
        // keyboard focus the focusin handler has already opened the panel — so
        // refresh the snapshot here or the key press would read a stale value.
        toggle.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            openAtPress = group.classList.contains('is-open');
          }
        });

        toggle.addEventListener('click', function () {
          if (!openAtPress) closeOthers(group);
          setOpen(group, !openAtPress);
          openAtPress = !openAtPress;
        });
      }

      group.addEventListener('pointerenter', function () {
        if (!desktopHover.matches) return;
        closeOthers(group);
        setOpen(group, true);
      });

      group.addEventListener('pointerleave', function () {
        if (!desktopHover.matches) return;
        if (group.contains(document.activeElement)) return;
        setOpen(group, false);
      });

      group.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape' || !group.classList.contains('is-open')) return;

        // The mobile menu's Escape handler is on document; stopping here keeps
        // this key press from also closing the whole hamburger menu around a
        // dealer who only meant to shut one group. Inside the collapsed menu
        // that gives the layered behaviour you would expect: first Escape closes
        // the group, second Escape closes the menu.
        e.stopPropagation();

        setOpen(group, false);
        if (trigger && document.activeElement !== trigger) {
          suppressFocusOpen = true;
          trigger.focus();
        }
      });
    });

    if (document.documentElement.dataset.penguinDealerDoc !== '1') {
      document.documentElement.dataset.penguinDealerDoc = '1';

      // A click that lands outside the nav entirely — focusout does not cover
      // this, because a click on non-focusable page furniture moves focus to
      // <body> without a relatedTarget the group can test.
      //
      // Desktop only, for the same reason closeOthers() is: below 1280px the
      // panels are in the collapsed menu, where "outside this group" includes
      // the other group's chevron, and closing on that would re-flow the menu
      // out from under the tap that was aimed at it.
      document.addEventListener('click', function (e) {
        if (!desktopLayout.matches) return;
        document.querySelectorAll('.dealer-nav__group.is-open').forEach(function (g) {
          if (!g.contains(e.target)) {
            g.classList.remove('is-open');
            var t = g.querySelector('.dealer-nav__toggle');
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        document.querySelectorAll('.dealer-nav__group.is-open').forEach(function (g) {
          g.classList.remove('is-open');
          var t = g.querySelector('.dealer-nav__toggle');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      });
    }
  }

  /* ---------- FAQ accordion (delegated) ---------- */
  function onFaqClick(e) {
    var btn = e.target.closest('.faq-question');
    if (!btn) return;

    var item = btn.closest('.faq-item');
    var list = btn.closest('.faq-list') || document;
    var wasActive = item.classList.contains('active');

    // Close siblings within this list only — not every .faq-item on the page.
    list.querySelectorAll('.faq-item.active').forEach(function (el) {
      el.classList.remove('active');
      var b = el.querySelector('.faq-question');
      if (b) b.setAttribute('aria-expanded', 'false');
    });

    if (!wasActive) {
      item.classList.add('active');
      btn.setAttribute('aria-expanded', 'true');
    }
  }

  /* ---------- Dealer cart count ----------
   *
   * Markup: snippets/header-cart.liquid, B2B sessions only. Add to cart is a
   * form post that lands on /cart, so the server-rendered count is normally
   * right. The exception is Back: a page restored from the back/forward cache
   * shows the count from before the add. Re-read /cart.js only in that case.
   */
  function refreshCartCount() {
    var link = document.querySelector('[data-header-cart]');
    if (!link || !window.fetch) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        var n = cart.item_count;
        var badge = link.querySelector('[data-header-cart-count]');
        link.setAttribute('aria-label', 'Cart, ' + n + (n === 1 ? ' item' : ' items'));
        if (badge) {
          badge.textContent = n > 99 ? '99+' : String(n);
          badge.hidden = n === 0;
        }
      })
      .catch(function () {});
  }

  function init(root) {
    initMobileMenu(root || document);
    initDealerNav(root || document);
  }

  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });
  document.addEventListener('click', onFaqClick);
  document.addEventListener('DOMContentLoaded', function () { init(document); });

  // The theme editor re-renders sections without reloading the page.
  // https://shopify.dev/docs/storefronts/themes/architecture/sections/section-schema
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
